import {ColorDecorator} from '@github-ui/shared-single-select-components/ColorDecorator'
import {testIdProps} from '@github-ui/test-id-props'
import {memo, useEffect, useRef} from 'react'

import {LoadingStates} from '../../../hooks/use-view-loading-state'
import {isSingleSelectOption, type VerticalGroup} from '../../../models/vertical-group'
import {AggregateLabels} from '../../common/aggregate-labels'
import {SanitizedHtml} from '../../dom/sanitized-html'
import styles from './column-name.module.css'

type Props = {
  columnLimit?: number
  hideItemsCount: boolean
  itemsCount: number
  loadingState: LoadingStates

  /** The vertical group whose name is displayed */
  verticalGroup: VerticalGroup

  /** A ref to the title element, used to programmatically focus a newly added column */
  triggerRef: React.RefObject<HTMLDivElement | null>

  /** Whether to focus on the name immediately on first render */
  initialFocus?: boolean
}

/**
 * Render a column name. Renaming is available exclusively through the column
 * action menu; the title itself is not interactive.
 */
export const ColumnName: React.FC<Props> = memo(
  ({columnLimit, hideItemsCount, itemsCount, verticalGroup, loadingState, initialFocus, triggerRef}) => {
    // Capture the initial focus intent once at construction time so the mount
    // effect reads a stable ref rather than a reactive prop.
    const initialFocusRef = useRef(initialFocus)

    useEffect(() => {
      // Focus the newly added column's title on mount. This is a genuine DOM
      // side-effect (imperative focus), so the effect is the right tool here.
      if (initialFocusRef.current) triggerRef.current?.focus()
    }, [triggerRef])

    return (
      <div
        ref={triggerRef}
        // Allow programmatic focus (e.g. focusing a newly added column) without
        // being a tab stop.
        tabIndex={-1}
        {...testIdProps('board-view-column-title')}
        className={styles.Box}
      >
        {isSingleSelectOption(verticalGroup.groupMetadata) && (
          <ColorDecorator color={verticalGroup.groupMetadata.color} />
        )}
        <h2 className={styles.Title}>
          <SanitizedHtml {...testIdProps('board-view-column-title-text')} className={styles.SanitizedHtml}>
            {verticalGroup.nameHtml}
          </SanitizedHtml>
        </h2>
        {loadingState === LoadingStates.loaded && (
          <AggregateLabels
            aggregates={[]}
            hideItemsCount={hideItemsCount}
            columnLimit={columnLimit}
            itemsCount={itemsCount}
            counterClassName={styles.AggregateLabels}
          />
        )}
      </div>
    )
  },
)

ColumnName.displayName = 'ColumnName'
